import React, { useState } from 'react';
import { StarIcon, PlayIcon, LightbulbIcon, HandIcon, ShieldCheckIcon, XIcon } from 'lucide-react';
import { AuthorSignature } from '../components/AuthorSignature';
type ModalType = 'terms' | 'privacy' | 'contact' | null;
export function PocketSurvivalKitPage() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <div className="min-h-screen w-full bg-[#f8f7f4] font-sans text-[#1e293b] selection:bg-[#b91c1c] selection:text-white" data-id="element-869">
      {/* 1. Top Nav Bar */}
      <header className="bg-[#002868] text-white w-full" data-id="element-870">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-12 flex items-center justify-between" data-id="element-871">
          <div className="flex items-center gap-2 font-bold text-lg tracking-tight" data-id="element-872">
            <StarIcon className="w-5 h-5 fill-current text-[#b91c1c]" data-id="element-873" />
            <span data-id="element-874">PatriotsInformer</span>
          </div>
        </div>
      </header>

      {/* 2. Breadcrumb Bar */}
      <div className="bg-white border-b border-gray-200 w-full" data-id="element-875"></div>

      {/* Main Content Area */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-12" style={{
      paddingTop: '2px'
    }} data-id="element-876">
        {/* 3. Article Section */}
        <article className="space-y-6" data-id="element-877">
          {/* Title & Meta */}
          <div className="space-y-2" data-id="element-878">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#0f172a] tracking-tight leading-tight" data-id="element-879">
              Build a Pocket Survival Kit (Small, Simple, Life-Saving)
            </h1>
          </div>

          {/* Author Bio */}
          <div className="flex flex-row items-center gap-4 sm:gap-6" data-id="element-880">
            <img src="https://cdn.magicpatterns.com/uploads/qCiNdfGtT6ARUfiGnS7VJm/jack-turner-avatar-2.jpg" alt="Jack Turner" className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-gray-200 shadow-sm flex-shrink-0" data-id="element-881" />
            <div className="text-left space-y-1" data-id="element-882">
              <h3 className="font-bold text-[#0f172a] text-lg" data-id="element-883">Jack Turner</h3>
              <p className="text-gray-500 text-sm" data-id="element-884">
                Head of research at PatriotsInformer.com
              </p>
              <p className="text-gray-400 text-xs uppercase tracking-wide pt-1" data-id="element-885">
                May 5, 2026 · 4 min read
              </p>
            </div>
          </div>

          {/* Video Embed */}
          <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden shadow-xl" data-id="element-886">
            <iframe src="https://www.youtube.com/embed/IzNMLVEmKV4?modestbranding=1&rel=0&disablekb=1&fs=0&iv_load_policy=3&playsinline=1&controls=0" title="Video" className="w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" referrerPolicy="strict-origin-when-cross-origin" sandbox="allow-scripts allow-same-origin allow-presentation" data-id="element-887" />
          </div>

          {/* Article Body */}
          <div className="space-y-6 text-lg text-[#334155] leading-relaxed" data-id="element-888">
            <p data-id="element-889">
              There's something satisfying about{' '}
              <b data-id="element-890">putting together a kit that actually works</b>. Real
              components, real skills, real confidence that you're covered if
              things go sideways.
            </p>
            <p data-id="element-891">
              Once you've got that foundation solid, the natural next step is
              thinking bigger. Not just "what do I carry" but "what have I
              built."
            </p>
            <p data-id="element-892">
              And{' '}
              <b data-id="element-893">
                there's one build in particular that most people have never come
                across
              </b>
              . Not because it's complicated. Not because it's expensive.
            </p>
            <p data-id="element-894">
              But because it's <b data-id="element-895">been quietly used by the military and NASA</b>{' '}
              for decades and somehow never made it into the mainstream survival
              conversation.
            </p>
            <p data-id="element-896">
              The guys <b data-id="element-897">who've already built one don't talk about it much</b>.
              They just have water when nobody else does.
            </p>
          </div>

          {/* CTA Callout Box */}
          <div className="mt-12 border-4 border-dashed border-[#b91c1c] bg-yellow-50 p-6 sm:p-8 rounded-lg relative" data-id="element-898">
            <div className="space-y-4" data-id="element-899">
              <div className="flex items-start gap-3" data-id="element-900">
                <h3 className="text-xl font-bold text-[#0f172a]" data-id="element-901">
                  Want to build your own off-grid water source from scratch?
                </h3>
              </div>

              <p className="text-[#334155] text-lg leading-relaxed" data-id="element-902">
                This step-by-step guide shows you exactly how to create a device
                that pulls clean, drinkable water straight from the air, using
                cheap parts you can find at any hardware store.
              </p>

              <div className="pt-2" data-id="element-903">
                <a href="https://trk.adsdomredir.com/click/1" className="inline-flex items-center font-bold underline decoration-2 underline-offset-4 text-[20px] text-[#0000ff] gap-[1px]" data-id="element-904">
                  👉 See how it's built
                </a>
              </div>
            </div>
          </div>
        </article>

        {/* 4. 'You Might Also Like' Section */}
        <section className="space-y-8 pt-8 border-t border-gray-200" data-id="element-905">
          <h2 className="text-3xl font-bold text-[#0f172a]" data-id="element-906">
            You Might Also Like:
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8" data-id="element-907">
            {/* Card 1 */}
            <div className="group cursor-pointer space-y-3" data-id="element-908">
              <a href="https://trk.adsdomredir.com/click/2" className="block aspect-[4/3] overflow-hidden rounded-lg shadow-md" data-id="element-909">
                <img src="https://cdn.magicpatterns.com/uploads/tfBxRA9s5qgvhpKFp12h5o/unnamed_(7).jpg" alt="The Lost SuperFoods book" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-910" />
              </a>
              <h3 className="font-bold text-lg text-[#0f172a] leading-tight group-hover:text-[#002868] transition-colors" data-id="element-911">
                <a href="https://trk.adsdomredir.com/click/2" data-id="element-912">
                  Superfoods That You Can Store Without Refrigeration for Years
                </a>
              </h3>
            </div>

            {/* Card 2 */}
            <div className="group cursor-pointer space-y-3" data-id="element-913">
              <a href="https://trk.adsdomredir.com/click/3" className="block aspect-[4/3] overflow-hidden rounded-lg shadow-md" data-id="element-914">
                <img src="https://cdn.magicpatterns.com/uploads/aeW4oPiWoeR5Wzq6FuUxBZ/new5notext.png" alt="Vegetable garden" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-915" />
              </a>
              <h3 className="font-bold text-lg text-[#0f172a] leading-tight group-hover:text-[#002868] transition-colors" data-id="element-916">
                <a href="https://trk.adsdomredir.com/click/3" data-id="element-917">
                  The Underground Energy Method They Don't Want You To Know
                  About
                </a>
              </h3>
            </div>

            {/* Card 3 */}
            <div className="group cursor-pointer space-y-3" data-id="element-918">
              <a href="https://trk.adsdomredir.com/click/4" className="block aspect-[4/3] overflow-hidden rounded-lg shadow-md" data-id="element-919">
                <img src="https://cdn.magicpatterns.com/uploads/7ksskNxA12GTBLDP2Kydn9/img18.png" alt="Man holding tools" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-920" />
              </a>
              <h3 className="font-bold text-lg text-[#0f172a] leading-tight group-hover:text-[#002868] transition-colors" data-id="element-921">
                <a href="https://trk.adsdomredir.com/click/4" data-id="element-922">
                  Nature-Inspired 3D Solar Sphere Generates Energy On Demand
                  (Watch Video)
                </a>
              </h3>
            </div>
          </div>

          <AuthorSignature data-id="element-923" />
        </section>
      </main>

      {/* 5. Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12 py-12" data-id="element-924">
        <div className="max-w-4xl mx-auto px-4 text-center space-y-6" data-id="element-925">
          <div className="flex items-center justify-center gap-2 text-[#b91c1c]" data-id="element-926">
            <div className="relative w-10 h-10" data-id="element-927">
              <StarIcon className="w-10 h-10 fill-current text-[#002868]" data-id="element-928" />
              <StarIcon className="w-4 h-4 fill-current text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-929" />
            </div>
          </div>

          <div className="text-xs text-gray-500 space-y-4 max-w-2xl mx-auto leading-relaxed" data-id="element-930">
            <p data-id="element-931">
              © 2018-2026 PatriotsInformer Media, LLC | All Rights Reserved |
              63 N. Burritt - Buffalo, WY 82834 United States
            </p>
            <p className="space-x-2" data-id="element-932">
              <button onClick={() => setActiveModal('terms')} className="underline hover:text-[#002868] cursor-pointer" data-id="element-933">
                Terms & Conditions
              </button>
              <span data-id="element-934">|</span>
              <button onClick={() => setActiveModal('privacy')} className="underline hover:text-[#002868] cursor-pointer" data-id="element-935">
                Privacy Policy
              </button>
              <span data-id="element-936">|</span>
              <button onClick={() => setActiveModal('contact')} className="underline hover:text-[#002868] cursor-pointer" data-id="element-937">
                Contact Us
              </button>
            </p>
            <p data-id="element-938">
              No part of this report may be reproduced or placed on any
              electronic medium without written permission from the publisher.
              Information contained herein is obtained from sources believed to
              be reliable, but its accuracy cannot be guaranteed.
            </p>
          </div>
        </div>
      </footer>

      {/* Modals */}
      {activeModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setActiveModal(null)} data-id="element-939">
          <div className="absolute inset-0 bg-black/50" data-id="element-940" />
          <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-941">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between" data-id="element-942">
              <h2 className="text-lg font-bold text-[#0f172a]" data-id="element-943">
                {activeModal === 'terms' && 'Terms & Conditions'}
                {activeModal === 'privacy' && 'Privacy Policy'}
                {activeModal === 'contact' && 'Contact Us'}
              </h2>
              <button onClick={() => setActiveModal(null)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors" data-id="element-944">
                <XIcon className="w-5 h-5 text-gray-500" data-id="element-945" />
              </button>
            </div>

            <div className="px-6 py-6 text-sm text-[#334155] leading-relaxed space-y-4" data-id="element-946">
              {activeModal === 'terms' && <>
                  <p className="font-semibold text-[#0f172a]" data-id="element-947">
                    Last Updated: February 2026
                  </p>
                  <p data-id="element-948">
                    Welcome to PatriotsInformer.com. By accessing or using our
                    website, you agree to be bound by these Terms & Conditions.
                    Please read them carefully before using our services.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-949">
                    1. Acceptance of Terms
                  </h3>
                  <p data-id="element-950">
                    By accessing this website, you acknowledge that you have
                    read, understood, and agree to be bound by these Terms &
                    Conditions. If you do not agree, please do not use this
                    website.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-951">
                    2. Use of Content
                  </h3>
                  <p data-id="element-952">
                    All content on this website, including text, graphics,
                    images, and other material, is for informational purposes
                    only. No part of this report may be reproduced or placed on
                    any electronic medium without written permission from the
                    publisher.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-953">
                    3. Disclaimer
                  </h3>
                  <p data-id="element-954">
                    Information contained herein is obtained from sources
                    believed to be reliable, but its accuracy cannot be
                    guaranteed. The content is not intended as professional
                    advice. Always consult qualified professionals for specific
                    guidance.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-955">
                    4. Limitation of Liability
                  </h3>
                  <p data-id="element-956">
                    PatriotsInformer Media, LLC shall not be liable for any
                    damages arising from the use or inability to use the
                    materials on this website.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-957">
                    5. Governing Law
                  </h3>
                  <p data-id="element-958">
                    These terms shall be governed by and construed in accordance
                    with the laws of the State of Texas, without regard to its
                    conflict of law provisions.
                  </p>
                  <p className="pt-2 text-xs text-gray-400" data-id="element-959">
                    © 2018-2026 PatriotsInformer Media, LLC. All Rights
                    Reserved.
                  </p>
                </>}

              {activeModal === 'privacy' && <>
                  <p className="font-semibold text-[#0f172a]" data-id="element-960">
                    Last Updated: February 2026
                  </p>
                  <p data-id="element-961">
                    PatriotsInformer Media, LLC ("we," "us," or "our") is
                    committed to protecting your privacy. This Privacy Policy
                    explains how we collect, use, and safeguard your
                    information.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-962">
                    1. Information We Collect
                  </h3>
                  <p data-id="element-963">
                    We may collect personal information such as your name and
                    email address when you voluntarily submit it through our
                    opt-in forms. We also collect non-personal information such
                    as browser type, IP address, and pages visited.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-964">
                    2. How We Use Your Information
                  </h3>
                  <p data-id="element-965">
                    We use the information we collect to send you the content
                    you requested, communicate updates and offers, improve our
                    website and services, and comply with legal obligations.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-966">
                    3. Email Communications
                  </h3>
                  <p data-id="element-967">
                    By providing your email address, you consent to receive
                    emails from us. You can unsubscribe at any time by clicking
                    the "unsubscribe" link at the bottom of any email.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-968">
                    4. Third-Party Sharing
                  </h3>
                  <p data-id="element-969">
                    We do not sell, trade, or rent your personal information to
                    third parties. We may share information with trusted service
                    providers who assist us in operating our website.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-970">
                    5. Data Security
                  </h3>
                  <p data-id="element-971">
                    We implement reasonable security measures to protect your
                    personal information. However, no method of transmission
                    over the Internet is 100% secure.
                  </p>
                  <p className="pt-2 text-xs text-gray-400" data-id="element-972">
                    © 2018-2026 PatriotsInformer Media, LLC. All Rights
                    Reserved.
                  </p>
                </>}

              {activeModal === 'contact' && <>
                  <p data-id="element-973">
                    We'd love to hear from you. Feel free to reach out using the
                    information below:
                  </p>
                  <div className="bg-[#f8f7f4] rounded-lg p-5 space-y-3 border border-gray-200" data-id="element-974">
                    <h3 className="font-bold text-[#0f172a]" data-id="element-975">
                      PatriotsInformer Media, LLC
                    </h3>
                    <p data-id="element-976">
                      10160 Highway 242 Ste 800
                      <br data-id="element-977" />
                      PMB 1089
                      <br data-id="element-978" />
                      Conroe, TX 77385
                    </p>
                    <p data-id="element-979">
                      <span className="font-semibold text-[#0f172a]" data-id="element-980">
                        Email:
                      </span>{' '}
                      <a href="mailto:support@patriotsinformer.com" className="text-[#002868] underline" data-id="element-981">
                        support@patriotsinformer.com
                      </a>
                    </p>
                  </div>
                  <p className="pt-2 text-xs text-gray-400" data-id="element-982">
                    © 2018-2026 PatriotsInformer Media, LLC. All Rights
                    Reserved.
                  </p>
                </>}
            </div>
          </div>
        </div>}
    </div>;
}